import { TextInput } from '@patternfly/react-core'
import * as React from 'react'
import { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import styled from 'styled-components'

import {
  getQuestionValidators,
  getResponseErrorsForStringValidators,
  isResponseValid,
} from '@app/domain/form/form.helpers'
import type { Question } from '@app/domain/instrument'
import { ValidationStatusId } from '@app/lib/validators'
import type { AppState } from '@app/state'
import type { QuestionsConfig } from '@app/state/config/interfaces'

import { FormGroup } from '../form-inputs'

const TextField = styled(TextInput)`
  padding: 8px 16px 8px 16px;
  border: 2px solid ${(props) => props.theme.colors.primary};
  border-radius: 5px;
  display: inline-block;
  vertical-align: middle;
  appearance: none;
  flex: 1 0 auto;
  max-width: 80%;
  margin: 0 auto;
`

const FormGroupHelperTextInvalid = styled.label`
  margin-left: 10%;
  color: #a30000;
`

interface Props {
  onChange: (value: string) => void
  question: Question
  value: string
}

export const TextQuestion = ({ onChange, question, value }: Props) => {
  const questionResponsesConfig = useSelector<AppState, QuestionsConfig>(
    (state) => state.config.api.questions,
  )

  const validators = useMemo(() => {
    return getQuestionValidators(question)
  }, [question])

  const [stringValue, setStringValue] = useState<string>(value)
  const [textInputValidation, setTextInputValidation] = useState<
    ValidationStatusId
  >(
    !value
      ? ValidationStatusId.Default
      : isResponseValid(question, value, questionResponsesConfig)
      ? ValidationStatusId.Default
      : ValidationStatusId.Error,
  )
  const [errorMessage, setErrorMessage] = useState<string>(
    !value
      ? null
      : getResponseErrorsForStringValidators(
          validators,
          value,
          questionResponsesConfig,
        )[0],
  )

  const handleBlur = useCallback(() => {
    onChange(stringValue)
  }, [onChange, stringValue])

  const handleChange = useCallback(
    (updatedStringValue: string) => {
      setStringValue(updatedStringValue)
      let inputIsValid = false
      if (updatedStringValue !== undefined) {
        const errors = getResponseErrorsForStringValidators(
          validators,
          updatedStringValue,
          questionResponsesConfig,
        )
        inputIsValid = errors.length === 0
        setErrorMessage(errors[0])
      }

      inputIsValid
        ? setTextInputValidation(ValidationStatusId.Default)
        : setTextInputValidation(ValidationStatusId.Error)

      onChange(updatedStringValue)
    },
    [onChange, validators, questionResponsesConfig],
  )

  return (
    <FormGroup
      fieldId={question.id}
      label={question.label}
      details={question.details}
      stretch={'stretch'}
    >
      <TextField
        id={question.id}
        name={question.id}
        value={stringValue}
        validated={textInputValidation}
        onBlurCapture={handleBlur}
        onChange={handleChange}
      />
      <FormGroupHelperTextInvalid>{errorMessage}</FormGroupHelperTextInvalid>
    </FormGroup>
  )
}
